/**
 * LCFPIPageShell.js — Production-grade page wrapper
 * Provides: loading skeleton · error state · empty state · error boundary
 * Used across all LCFPI pages for consistent production UX.
 */
import React, { Component } from "react";
import { AlertTriangle, RefreshCw, Plus, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";

// ── Loading Skeleton ──────────────────────────────────────────────────────────
export function LCFPILoadingSkeleton({ rows = 5, showHeader = true }) {
  return (
    <div className="p-6 animate-pulse" data-testid="lcfpi-loading-skeleton">
      {showHeader && (
        <div className="mb-6">
          <div className="h-4 w-24 bg-slate-200 rounded mb-3" />
          <div className="h-8 w-72 bg-slate-200 rounded mb-2" />
          <div className="h-4 w-96 bg-slate-100 rounded" />
        </div>
      )}
      {/* KPI strip skeleton */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="h-3 w-20 bg-slate-200 rounded mb-3" />
            <div className="h-7 w-16 bg-slate-200 rounded mb-2" />
            <div className="h-3 w-24 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
      {/* Content skeleton */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {[...Array(rows)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-50 last:border-0">
            <div className="h-4 w-24 bg-slate-200 rounded flex-shrink-0" />
            <div className="h-4 flex-1 bg-slate-100 rounded" />
            <div className="h-4 w-16 bg-slate-200 rounded flex-shrink-0" />
            <div className="h-6 w-14 bg-slate-100 rounded flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Error State ───────────────────────────────────────────────────────────────
export function LCFPIErrorState({ message, onRetry, title = "Unable to load data" }) {
  return (
    <div className="flex items-center justify-center min-h-64 p-8" data-testid="lcfpi-error-state">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={20} className="text-red-400" />
        </div>
        <p className="text-sm font-bold text-slate-700 mb-2">{title}</p>
        <p className="text-xs text-slate-400 mb-5 leading-relaxed">{message || "An error occurred loading this data. Check your connection and try again."}</p>
        {onRetry && (
          <button onClick={onRetry}
            className="flex items-center gap-2 bg-blue-600 text-white text-xs px-4 py-2 rounded-lg font-bold hover:bg-blue-700 mx-auto"
            data-testid="error-retry-btn">
            <RefreshCw size={11} /> Retry
          </button>
        )}
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
export function LCFPIEmptyState({
  title = "No data yet",
  subtitle = "Get started by adding the first item.",
  actionLabel = null,
  actionLink = null,
  icon: Icon = Inbox,
}) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center min-h-64 p-8" data-testid="lcfpi-empty-state">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon size={22} className="text-slate-300" />
        </div>
        <p className="text-sm font-bold text-slate-600 mb-2">{title}</p>
        <p className="text-xs text-slate-400 mb-5 leading-relaxed">{subtitle}</p>
        {actionLabel && actionLink && (
          <button onClick={() => navigate(actionLink)}
            className="flex items-center gap-2 bg-blue-600 text-white text-xs px-4 py-2 rounded-lg font-bold hover:bg-blue-700 mx-auto"
            data-testid="empty-state-action">
            <Plus size={11} /> {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Error Boundary ────────────────────────────────────────────────────────────
export class LCFPIErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[LCFPI Error Boundary]", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8" data-testid="lcfpi-error-boundary">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto text-center">
            <AlertTriangle size={24} className="text-red-400 mx-auto mb-3" />
            <p className="text-sm font-bold text-red-700 mb-2">Page Error</p>
            <p className="text-xs text-red-500 mb-4 font-mono">{this.state.error?.message?.substring(0, 120)}</p>
            <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              className="bg-red-600 text-white text-xs px-4 py-2 rounded-lg font-bold hover:bg-red-700">
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Page Shell (convenience wrapper) ─────────────────────────────────────────
export default function LCFPIPageShell({
  loading, error, onRetry, isEmpty, emptyTitle, emptySubtitle, emptyActionLabel, emptyActionLink,
  loadingRows = 5, EmptyIcon = Inbox, children,
}) {
  if (loading) return <LCFPILoadingSkeleton rows={loadingRows} />;
  if (error) return <LCFPIErrorState message={error} onRetry={onRetry} />;
  if (isEmpty) return <LCFPIEmptyState title={emptyTitle} subtitle={emptySubtitle} actionLabel={emptyActionLabel} actionLink={emptyActionLink} icon={EmptyIcon} />;
  return <>{children}</>;
}
